import React, { forwardRef } from 'react';
import './StatusBar.css';

const StatusBar = forwardRef(({
  videos,
  loading,
  error,
  summarizing,
  allVoices = [],
  selectedVoice,
  onChangeVoice,
  ttsSpeed,
  onChangeSpeed,
  voiceLanguageFilter = 'all',
  onChangeVoiceLanguageFilter,
  onReload,
}, ref) => {
  const videoCount = videos.length;
  const summarizingCount = summarizing.size;

  const languagesSorted = React.useMemo(() => {
    const set = new Set(allVoices.map(v => v.lang.split('-')[0]));
    return ['all', ...Array.from(set).sort()];
  }, [allVoices]);

  const voicesForSelect = React.useMemo(() => 
    voiceLanguageFilter === 'all'
      ? allVoices
      : allVoices.filter(v => v.lang.startsWith(voiceLanguageFilter))
  , [allVoices, voiceLanguageFilter]);

  const getLangName = (code) => {
    try {
      if (code === 'all') return 'All Languages';
      const lang = new Intl.DisplayNames(['en'], {type: 'language'});
      return lang.of(code);
    } catch(e) {
      return code;
    }
  }

  return (
    <div className="status-bar-container" ref={ref}>
      {error && (
        <div className="error-bar">
          <span className="icon">⚠️</span> {error}
        </div>
      )}
      <div className="status-bar">
        {/* Status info area */}
        <div className="status-item-group status-info">
          <div className="status-item" title="Number of videos loaded">
            <span className="icon">🎬</span>
            <span className="status-value">{videoCount}</span>
          </div>
          {onReload && (
            <button
              onClick={onReload}
              className="reload-btn"
              disabled={loading}
              title="Reload videos"
            >
              ↻
            </button>
          )}
          {summarizingCount > 0 && (
            <div className="status-item" title={`${summarizingCount} video(s) being processed`}>
              <span className="icon">⏳</span>
              <span className="status-value loading">{summarizingCount}</span>
            </div>
          )}
          {loading && !summarizingCount && (
            <div className="status-item">
              <span className="status-value loading">Loading...</span>
            </div>
          )}
        </div>

        {/* Speed area */}
        <div className="control-group speed speed-control">
          <span className="icon">⏩</span>
          <input
            id="speed-range"
            type="range"
            title={`Playback speed: ${ttsSpeed}x`}
            min={0.25}
            max={2.0}
            step={0.05}
            value={ttsSpeed || 1}
            onChange={e => onChangeSpeed && onChangeSpeed(Number(e.target.value))}
          />
          <span className="speed-value">{ttsSpeed?.toFixed(2)}x</span>
        </div>

        {/* Language filter area */}
        <div className="control-group lang lang-control">
          <span className="icon">🌐</span>
          <select
            id="lang-select"
            title="Filter by language"
            value={voiceLanguageFilter}
            onChange={e => onChangeVoiceLanguageFilter && onChangeVoiceLanguageFilter(e.target.value)}
          >
            {languagesSorted.map(l => (
              <option key={l} value={l}>{getLangName(l)}</option>
            ))}
          </select>
        </div>

        {/* Voice select area */}
        <div className="control-group voice voice-control">
          <span className="icon">🎙️</span>
          <select
            id="voice-select"
            title="Select a voice"
            value={selectedVoice || ''}
            onChange={e => onChangeVoice && onChangeVoice(e.target.value)}
          >
            {allVoices.length === 0 && <option>Loading…</option>}
            {voicesForSelect.map(v => (
              <option key={v.id || v.value} value={v.id || v.value}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
});

export default StatusBar;
